import { useWizard } from "components/elements/Wizard/useWizard";
import SpeckleTitle from "../../SpeckleTitle";
import SpeckleBreadcrumbs, { SpeckleCrumb } from "../../SpeckleBreadcrumbs";
import InputSearch from "components/elements/Input/InputSearch";
import { ErrorMessage, Field, useFormikContext } from "formik";
import { nameof } from "lib/actions/util/nameof";
import { useSpeckleAccounts } from "lib/hooks/useSpeckleAccounts";
import SpeckleRadioInputAccounts from "../../controls/SpeckleRadioInputAccounts";
import { useState } from "react";
import { SpeckleImportModel } from "../../types/SpeckleImportModel";
import Loading from "components/elements/Loading/Loading";
import FormError from "components/elements/Forms/FormError";
import { getModelTextSearch } from "lib/actions/getModelTextSearch";
import { SpeckleAccount } from "lib/types/speckle/SpeckleAccount";
import Button from "components/elements/Button/Button";

export default function SpeckleAccountsListStepVariant(p: {
  isSubmitting: boolean;
  onSelect: (value: string) => void;
}) {
  const { values, isSubmitting } = useFormikContext<SpeckleImportModel>();
  const [searchTerm, setSearchTerm] = useState("");
  const { stepViewVariant } = useWizard();
  const { accounts: accountsData, isLoading, hasError } = useSpeckleAccounts();
  const { search } = getModelTextSearch<SpeckleAccount>(
    searchTerm,
    (account) => account.email,
  );
  const accounts = search(accountsData) || [];
  const accountItems =
    !isLoading &&
    !hasError &&
    accounts.map((x) => ({
      value: x.model.id.toString(),
      label: x.model.email,
      description: x.model.serverUrl,
    }));

  async function onSelect(value: string) {
    p.onSelect(value);
    stepViewVariant("account-validate");
  }

  return (
    <div className="grid grid-cols-2 gap-8">
      <div>
        <header>
          <SpeckleTitle />
          <h1 className="mt-10 text-2xl font-thin leading-loose">
            Select a <span className="font-normal">Speckle Account</span> <br />
            <span className="flex whitespace-nowrap">
              or&nbsp;
              <Button
                slim
                type="button"
                className="my-auto ml-2 h-8 text-sm"
                disabled={p.isSubmitting}
                onClick={() => stepViewVariant("add-account")}
              >
                add account
              </Button>
            </span>
          </h1>
        </header>
      </div>
      <div className="space-y-5">
        <SpeckleBreadcrumbs>
          <SpeckleCrumb active>Accounts</SpeckleCrumb>
        </SpeckleBreadcrumbs>
        <div className="flex items-center justify-between pr-5 text-xs">
          <span>{accountItems?.length ?? 0} accounts available</span>
        </div>
        <div className="h-[36rem] select-none space-y-8 overflow-y-auto pb-8 pr-4">
          <InputSearch
            defaultValue={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search Accounts"
          />
          <Field
            name={nameof(values, "connectedAccountId")}
            as={SpeckleRadioInputAccounts}
            items={accountItems}
            onSelect={onSelect}
            disabled={isSubmitting}
          />
          {isLoading && <Loading />}
          {hasError && <span className="text-center">Error</span>}
        </div>
        <ErrorMessage
          name={nameof(values, "connectedAccountId")}
          component={FormError}
        />
      </div>
    </div>
  );
}
